import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Megaphone, Users, Calendar, BarChart3, Target, Link2 } from 'lucide-react';
import { useBroadcastNotifications } from '@/hooks/useBroadcastNotifications';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

export const BroadcastHistory = () => {
  const { broadcasts, isLoading } = useBroadcastNotifications();

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'normal': return 'bg-blue-500';
      case 'low': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getTargetTypeLabel = (type: string) => {
    switch (type) {
      case 'all': return 'جميع الموظفين';
      case 'role': return 'حسب الدور';
      case 'department': return 'حسب القسم';
      case 'specific_employees': return 'موظفين محددين';
      default: return type;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          سجل الإشعارات الجماعية
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px] pr-4">
          {broadcasts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              لا توجد إشعارات جماعية مرسلة بعد
            </div>
          ) : (
            <div className="space-y-4">
              {broadcasts.map((broadcast) => (
                <div key={broadcast.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h4 className="font-semibold text-lg">{broadcast.title}</h4>
                      <p className="text-sm text-muted-foreground">{broadcast.message}</p>
                    </div>
                    <Badge className={`${getPriorityColor(broadcast.priority)} text-white`}>
                      {broadcast.priority === 'urgent' ? 'عاجل' :
                       broadcast.priority === 'high' ? 'مرتفع' :
                       broadcast.priority === 'normal' ? 'عادي' : 'منخفض'}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Target className="h-4 w-4" />
                      <span>{getTargetTypeLabel(broadcast.target_type)}</span>
                      {broadcast.target_value && (
                        <Badge variant="outline" className="ml-1">
                          {broadcast.target_value}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{broadcast.total_recipients} مستقبل</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <BarChart3 className="h-4 w-4" />
                      <span>{broadcast.read_count} قراءة</span>
                    </div>
                  </div>

                  {broadcast.link && (
                    <div className="flex items-center gap-1 text-sm">
                      <Link2 className="h-4 w-4 text-muted-foreground" />
                      <a href={broadcast.link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {broadcast.link}
                      </a>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>
                        {format(new Date(broadcast.created_at), 'dd MMMM yyyy - HH:mm', { locale: ar })}
                      </span>
                    </div>
                    
                    {broadcast.expires_at && (
                      <Badge variant="outline" className="text-xs">
                        ينتهي: {format(new Date(broadcast.expires_at), 'dd/MM/yyyy')}
                      </Badge>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {broadcast.channels.map(channel => (
                      <Badge key={channel} variant="secondary" className="text-xs">
                        {channel === 'in_app' ? 'داخل التطبيق' :
                         channel === 'email' ? 'بريد إلكتروني' :
                         channel === 'sms' ? 'رسائل SMS' : channel}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};